import type { PaymentStatus } from '@/api/interfaces/payment.api.d';

export type BitBoolean = 0 | 1;

export type SSN = string;

export type UUID = `${string}-${string}-${string}-${string}-${string}`;

export enum PortalTypeEnum {
    CLIENT = 'client',
    COMPANY = 'company',
    EMPLOYEE = 'employee',
}

export type PortalType = PortalTypeEnum[keyof PortalTypeEnum];

type Role = 'agent' | 'attorney' | 'buyer' | 'lender' | 'other' | 'seller';

export type WireDoc = {
    created_at: string;
    completed?: boolean;
    created_at_timestamp: number;
    download_url: string;
    uploader_id?: number;
    uploader_type: string;
    id: number;
    is_wire: boolean;
    viewed_at?: string;
    is_signed: boolean;
    signed_at?: string;
    name: string;
    user_id: number;
    user_name: string;
};

export type EsignWireDoc = WireDoc & {
    esign_link?: string;
    need_to_sign?: boolean;
    completedUrl?: string;
};

export type ClientDocumentUploaderType = 'user' | 'client';

export type AnyDocument = DocumentDetails | UserUploadedDocument;

export type DocumentDetails = EsignWireDoc & {
    show?: boolean;
    status?: string;
    type?: ClientDocumentUploaderType;
};

export interface UserUploadedDocument {
    id: number;
    name: string;
    signed_at: string | null;
    viewed_at: string | null;
    created_at: string;
    created_at_timestamp: number;
    is_signed: boolean;
    need_to_sign: boolean;
    esign_link: string | null;
    uploader_id: number;
    uploader_type: string;
    user_name: string;
    download_url: string;
    // the following properties are absent in this type of document
    is_wire: undefined;
    type: undefined;
    show?: undefined;
    user_id: undefined;
    status?: undefined;
}

export type Client = {
    email: string;
    role: Role;
    id?: string;
    phone?: string;
    first?: string;
    last?: string;
};

export interface MultipleSelectClient extends Client {
    value?: string;
    text?: string;
}

export interface Company {
    id: number;
    name: string;
}

export interface DsSingleSelectCompanyOption extends Company {
    label: Company['name'];
    value: Company['id'];
}

export interface Branch {
    id: number;
    name: string;
    title: Company;
}

export interface DsSingleSelectBranchOption extends Branch {
    label: Branch['name'];
    value: Branch['id'];
}

export type ClosinglockCreateFileForm = {
    _token: string;
    amount_down: string;
    earnest_money: string;
    sales_price: string;
    vendor: string;
    vendor_order_id: string;
    vendor_transaction_id: string;
    company_id: string;
    branch_id: string;
    address: string;
    ref_number: string;
    balance_due: string; // cents
    close_date: string;
    [key: `first[${number}]`]: string;
    [key: `last[${number}]`]: string;
    [key: `email[${number}]`]: string;
    [key: `phone[${number}]`]: string;
    [key: `role[${number}]`]: Role;
    notes: string;
    wireDoc_id: string;
    'wireDoc_recipients[]': string[];
    'fileNotificationUserIds[]': string[];
    'requestBankInfo_recipients[]': string[];
    'requestIdVerification_recipients[]': string[];
    [key: `documents[${number}]`]: string;
    [key: `documents_recipients[${number}][]`]: string[];
    [key: `documents_sign_edit[${number}]`]: string;
    // notificationUsers: NotificationUser[],
    notify: string;
};

export interface AggregratedClientArrays {
    clients: Pick<Client, 'first' | 'last' | 'email' | 'phone' | 'role'>[];
}

export const enum Insurance {
    INSURANCE_INSURED = 'insuranceInsured',
    INSURANCE_PENDING = 'insurancePending',
    INSURANCE_ACTION_REQUIRED = 'insuranceActionRequired',
    INSURANCE_NOT_INSURED = 'insuranceNotInsured',
    /** Legacy status; no longer produced but still present on backfilled BAV reports. */
    INSURANCE_DISABLED = 'insuranceDisabled',
}

/** from `/file_notification/get_users` */
export interface NotificationUser {
    id: number;
    branch_id: number;
    name: string;
    email: string;
}

export interface PreFilePaymentWireDoc {
    id: number;
    active: boolean;
    branch_id: number;
    path: string;
    name: string;
    payment_start_date: string; // ISO date string
    deleted_at: string | null; // ISO date string
    insured: Insurance;
    disabled_at: string | null; // ISO date string
}

/** TODO: this interface is duplicated and it causes trouble in a bunch of places that have mixed usage
 * between this one and the `PreFilePayment` interface in `early-deposit-types.d.ts`.
 * This should be unified in a single place
 */
export interface PreFilePayment {
    id: string;
    payment_base_amount: number; // pennies
    fee_amount: number; // pennies
    payment_total_amount: number; // pennies
    idempotency_key: string;
    status: PaymentStatus;
    confirmation_number: number;
    payment_sent_date: string; // ISO date string
    effective_date: string; // ISO date string
    expires_at: string; // ISO date string
    payment_created_at: string; // ISO date string
    type: string;
    created_at: string; // ISO date string
    updated_at: string; // ISO date string
    wire_doc_id: number;
    payment_account_id: number;
    pre_file_id: number;
    insured: Insurance;
    wire_doc: PreFilePaymentWireDoc;
}

export type TpsIntegration = 'none' | 'resware' | 'softpro' | 'ramquest' | 'settlor';
export type SsoIntegration = 'azure' | 'saml2';
export type { Role };
